import { Autocomplete, Button, ButtonGroup, Checkbox, CreateCustomerModal, CreditCardModal, CurrencyInput, DatePicker, Grid, Input, Layout, NumericInput, Typography, Loader } from "../../../components";
import { Paper } from "../../../components";
import { useFormik } from "formik";
import { CreateOrderValidationSchema } from "@next-pontos/validations";
import { useAPI, useAxios, useToggle } from "../../../hooks";
import {useEffect, useMemo, useCallback, useState, Fragment, useContext, useRef} from 'react'
import useSWR from 'swr'
import { Box, FormControlLabel, IconButton, Link, Tooltip, Alert, AlertTitle } from "@mui/material";
import { formatCNPJ, formatCPF, formatDate, formatMoney } from "../../../utils";
import {  useParams } from "react-router-dom";
import API from "../../../API";
import moment from "moment";
import { AIRLINES_OPTIONS, COMPANY_OPTIONS } from "../../../constants";
import currency from "currency.js";
import VisibilityIcon from '@mui/icons-material/Visibility';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import TelegramIcon from '@mui/icons-material/Telegram';
import AuthContext from "../../../context/auth";
import { APP_NAME } from "@next-pontos/settings";
import { Add, ContentCopy, Email } from "@mui/icons-material";
import { enqueueSnackbar } from "notistack";
import useWebSocket, { ReadyState } from 'react-use-websocket';

export function NewOrder () { 
    const [creditCardModalVisibility, creditCardModalVisibilityControls] = useToggle(false)
    const { me } = useContext(AuthContext)
    const params = useParams()
    const bid = useSWR(`/bid/${params.id}`, (route) => params.id ? API.get(route).then(result => result.data): null)
    const cards = useSWR(`/card`, (route) => API.get(route).then(result => result.data))
    const [customers, refetchCustomers] = useAxios(`/customer`)
    const [createCustomerModalVisibility, createCustomerModalVisibilityControls] = useToggle(false)

    const { readyState, lastJsonMessage } = useWebSocket<{token: string}>(
        bid?.data?.account_id && bid.data.program.needs_email_redirect ? `${process.env.REACT_APP_API_URL}/ws/account/${bid?.data?.account_id}/token` : null, 
        { retryOnError: true, shouldReconnect: () => true,
    });
    
    useEffect(() => {
        if (readyState === ReadyState.OPEN) {
            const token = lastJsonMessage?.token

            if (token) {
                setAccountTokens(state => {
                    if (state[0] === token) {
                        return state
                    }

                    enqueueSnackbar('Novo token recebido!', {variant: 'success'})

                    return [
                        token,
                        ...state
                    ]
                })
            }

        }
    }, [lastJsonMessage, readyState])

    const [passwordData, setPasswordData] = useState<null | { password: string, secondary_password: string}>(null)
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [accountTokens, setAccountTokens] = useState<string[]>([])
    
    const passwordApi = useAPI()
    const orderApi = useAPI()
    
    const formik = useFormik({
        initialValues: {
            flight_date: '',
            airline: '',
            reservation_code: '',
            amount: '',
            fees: '',
            additional_cost: '',
            card_id: '',
            sell_price: '',
            pax: '',
            author_name: me?.name || '',
            pax_name: '',
            origin_airport_code: '',
            customer_id: '',
            description: '',
            to_receive: false,
            income_due_date: null,
        },
        onSubmit: async (values) => {
            setIsSubmitting(true);
            try {
                await orderApi.post(`/bid/${bid?.data?.id}/order`, values)
                
                window.location.replace(`/admin/ofertas`)
            } finally {
                setIsSubmitting(false);
            }
        },
        validateOnMount: true,
        validationSchema: CreateOrderValidationSchema
    })

    useEffect(() => {
        if (me?.name) {
            formik.setFieldValue('author_name', me.name)
        }
    }, [me?.name])
    
    const CARDS_OPTIONS = useMemo(() => {
        return (cards?.data || []).map((card: { id: number, last_four_digits: string, brand: string, alias: string }) => ({
            value: card.id,
            label: `**** ${card.last_four_digits} (${card.alias})`
        }))
    }, [cards])

    const totalCost = useMemo(() => {
        const amount_k = currency(formik.values.amount || 0, {precision: 3}).divide(1000).value
        const price_per_k = bid?.data?.price_per_k || 0
        const fees = formik.values.fees || 0
        const additional_cost = formik.values.additional_cost || 0

        const miles_cost = currency(amount_k, {precision: 3}).multiply(price_per_k)
        const final_cost = miles_cost.add(fees).add(additional_cost)

        return final_cost.value
    }, [formik, bid])

    const airlineOptions = useMemo(() => {
        return AIRLINES_OPTIONS.map(item => ({
            value: item.label,
            label: item.label
        }))
    }, [])

    const handleRevealPassword = useCallback(async () => {
        const passwordResponse = await passwordApi.get(`/account/${bid?.data?.account_id}/password`)
        setPasswordData(passwordResponse.data)
    }, [passwordApi, bid?.data?.account_id])

    const paxOptions = useMemo(() => {
        const maxPax = bid?.data?.pax || 9
        
        const maxPaxOptions = (new Array(maxPax).fill(0)).map((_, index) => {
            return {
                label: (index + 1).toString(),
                value: (index + 1).toString()
            }
        })

        if (!bid?.data?.pax) {
            return maxPaxOptions
        } else {
            const usedPax = bid?.data?.orders?.filter((item: {confirmation: string}) => item.confirmation === 'PENDING' || item.confirmation === 'CONFIRMED')?.map((item: {pax: number}) => item.pax)?.reduce((prev: number, curr: number) => prev + curr, 0)
            
            return maxPaxOptions.filter((_, index) => index < (maxPaxOptions.length - usedPax))
        }

    }, [bid?.data?.pax, bid?.data?.orders])

    const customersOptions = (customers?.data || []).map((customer: {name: string, id: string, is_business: boolean, document_number: string }) => ({
        label: `${customer.name} (${customer.is_business ? formatCNPJ(customer.document_number) : formatCPF(customer.document_number)})`,
        value: customer.id,
    }))

    const milesAvailable = useMemo(() => {
        const usedMiles = bid?.data?.orders?.filter((item: {confirmation: string}) => item.confirmation === 'PENDING' || item.confirmation === 'CONFIRMED')?.map((item: {miles: number}) => item.miles)?.reduce((prev: number, curr: number) => prev + curr, 0)
    
        return currency(bid?.data?.amount).subtract(usedMiles).value
    }, [bid?.data])

    const { amount, sell_price, fees, additional_cost } = formik.values
    const { setErrors }  = formik

    useEffect(() => {
        if (Number(amount) > Number(milesAvailable)) {
            setErrors({ amount: 'A quantidade de milhas deve ser menor que o disponível na oferta' })
        }
    }, [amount, milesAvailable, setErrors])

    const messageText = encodeURI(`Olá, ${bid?.data?.user?.name}! Tudo bem? Me chamo ${me?.name} e estou entrando em contato para falar da sua oferta número ${bid?.data?.id} na ${APP_NAME}.`)

    const soldCostPerK = useMemo(() => {
        const totalMilesCost = currency(sell_price || 0).subtract(fees || 0).subtract(additional_cost || 0).value
        
        return currency(totalMilesCost).divide(currency(amount || 0, {precision: 3}).divide(1000).value).value || 0
    }, [additional_cost, amount, fees, sell_price])

    const handleSubmit = async (values: any) => {
        setIsSubmitting(true);
        try {
            await formik.submitForm();
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Layout>
            {bid.isLoading ? (
                <div style={{
                    display: 'flex',
                    flex: '1',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: 'calc(100vh - 64px)'
                }}>
                    <Loader />
                </div>
            ) : (
                <Grid container spacing={4} minHeight={"calc(100vh - 64px)"} alignItems="flex-start" paddingTop={5} paddingX={4}>
                    <Grid item xs={12} lg={6}>
                        <Paper elevation={1} style={{border: '1px solid #e0e0e0'}}>
                            <form onSubmit={formik.handleSubmit}>
                                <Grid container padding={3} spacing={1}>
                                    <Grid item xs={12}>
                                        <Typography variant="h5" gutterBottom>Cadastrar Emissão</Typography>
                                    </Grid>

                                    <Grid item xs={12}>
                                        <Typography variant="subtitle1" fontWeight={500} gutterBottom>Dados do Cliente</Typography>
                                    </Grid>
                                    <Grid item xs={11}>
                                        <Autocomplete 
                                            label="Cliente"
                                            onChange={({value}) => formik.setFieldValue('customer_id', value, true)}
                                            options={customersOptions}
                                            onBlur={formik.handleBlur}
                                            value={customersOptions.find((item: { value: string }) => item.value === formik.values.customer_id) || null}
                                            helperText={formik.touched.customer_id && formik.errors.customer_id}
                                            error={!!formik.touched.customer_id && !!formik.errors.customer_id}
                                        />
                                    </Grid>
                                    <Grid item xs={1}>
                                        <Button 
                                            onClick={createCustomerModalVisibilityControls.setTrue}
                                            sx={{height: '100%'}}
                                        >
                                            <Add />
                                        </Button>
                                    </Grid>

                                    <Grid item xs={12} sx={{mt: 1}}>
                                        <Typography variant="subtitle1" fontWeight={500} gutterBottom>Detalhes do Voo</Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={4}>
                                        <DatePicker 
                                            label="Data do Voo"
                                            value={formik.values.flight_date ? moment(formik.values.flight_date) : null}
                                            onChange={(value) => formik.setFieldValue('flight_date', value?.toDate(), true)}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={4}>
                                        <Autocomplete 
                                            label="Companhia"
                                            onChange={({value}) => formik.setFieldValue('airline', value, true)}
                                            options={airlineOptions}
                                            onBlur={formik.handleBlur}
                                            value={airlineOptions.find(item => item.value === formik.values.airline)}
                                            helperText={formik.touched.airline && formik.errors.airline}
                                            error={!!formik.touched.airline && !!formik.errors.airline}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={4}>
                                        <Input 
                                            label="Emissor"
                                            name="author_name"
                                            onChange={formik.handleChange}
                                            value={formik.values.author_name}
                                            helperText={formik.touched.author_name && formik.errors.author_name}
                                            error={!!formik.touched.author_name && !!formik.errors.author_name}
                                            onBlur={formik.handleBlur}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={4}>
                                        <Input 
                                            label="Localizador"
                                            name="reservation_code"
                                            onChange={event => formik.setFieldValue('reservation_code', event.target.value.toUpperCase().trim())}
                                            value={formik.values.reservation_code}
                                            helperText={formik.touched.reservation_code && formik.errors.reservation_code}
                                            error={!!formik.touched.reservation_code && !!formik.errors.reservation_code}
                                            onBlur={formik.handleBlur}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={4}>
                                        <Input 
                                            label="Passageiros"
                                            name="pax"
                                            select
                                            options={paxOptions}
                                            onChange={formik.handleChange}
                                            value={formik.values.pax}
                                            helperText={formik.touched.pax && formik.errors.pax}
                                            error={!!formik.touched.pax && !!formik.errors.pax}
                                            onBlur={formik.handleBlur}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={4}>
                                        <Input
                                            label="Aeroporto de Origem"
                                            name="origin_airport_code"
                                            onChange={e => formik.setFieldValue('origin_airport_code', e.target.value.replace(/([^A-Z])/ig, '').toLocaleUpperCase().slice(0, 3), true)}
                                            onBlur={formik.handleBlur}
                                            value={formik.values.origin_airport_code}
                                            helperText={formik.touched.origin_airport_code && formik.errors.origin_airport_code}
                                            error={!!formik.touched.origin_airport_code && !!formik.errors.origin_airport_code}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={4}>
                                        <Tooltip title="Caso haja mais de um passageiro, insira o nome de apenas um deles." placement="top">
                                            <div>
                                                <Input
                                                    label="Nome do Passageiro"
                                                    name="pax_name"
                                                    onChange={formik.handleChange}
                                                    onBlur={formik.handleBlur}
                                                    value={formik.values.pax_name}
                                                    helperText={formik.touched.pax_name && formik.errors.pax_name}
                                                    error={!!formik.touched.pax_name && !!formik.errors.pax_name}
                                                />
                                            </div>
                                        </Tooltip>
                                    </Grid>

                                    <Grid item xs={12} sx={{mt: 1}}>
                                        <Typography variant="subtitle1" fontWeight={500} gutterBottom>Detalhes do pagamento</Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={4}>
                                        <NumericInput
                                            label="Milhas"
                                            name="amount"
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            value={formik.values.amount}
                                            helperText={formik.touched.amount && formik.errors.amount}
                                            error={!!formik.touched.amount && !!formik.errors.amount}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={4}>
                                        <CurrencyInput
                                            label="Taxas (R$)"
                                            name="fees"
                                            onChangeValue={(_, originalValue) => formik.setFieldValue('fees', originalValue, true)}
                                            onBlur={formik.handleBlur}
                                            value={formik.values.fees}
                                            helperText={formik.touched.fees && formik.errors.fees}
                                            error={!!formik.touched.fees && !!formik.errors.fees}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={4}>
                                        <Tooltip title="Bagagem despachada, marcação de assentos, etc...">
                                            <div>
                                                <CurrencyInput
                                                    label="Custos Adicionais (R$)"
                                                    name="additional_cost"
                                                    onChangeValue={(_, originalValue) => formik.setFieldValue('additional_cost', originalValue, true)}
                                                    onBlur={formik.handleBlur}
                                                    value={formik.values.additional_cost}
                                                    helperText={formik.touched.additional_cost && formik.errors.additional_cost}
                                                    error={!!formik.touched.additional_cost && !!formik.errors.additional_cost}
                                                />
                                            </div>
                                        </Tooltip>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={4}>
                                        <CurrencyInput
                                            label="Valor de Venda (R$)"
                                            name="sell_price"
                                            onChangeValue={(_, originalValue) => formik.setFieldValue('sell_price', originalValue, true)}
                                            onBlur={formik.handleBlur}
                                            value={formik.values.sell_price}
                                            helperText={formik.touched.sell_price && formik.errors.sell_price}
                                            error={!!formik.touched.sell_price && !!formik.errors.sell_price}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={4}>
                                        <Tooltip title="Cartão utilizado para pagamento das taxas." placement="top">
                                            <div>
                                                <Autocomplete 
                                                    label="Cartão"
                                                    onChange={({value}) => formik.setFieldValue('card_id', value, true)}
                                                    options={CARDS_OPTIONS}
                                                    onBlur={formik.handleBlur}
                                                    value={CARDS_OPTIONS.find((item: { value: string }) => item.value === formik.values.card_id) || null}
                                                    helperText={formik.touched.card_id && formik.errors.card_id}
                                                    error={!!formik.touched.card_id && !!formik.errors.card_id}
                                                />
                                            </div>
                                        </Tooltip>
                                    </Grid>

                                    <Grid item xs={12} sx={{mt: 1}}>
                                        <Typography variant="subtitle1" fontWeight={500} gutterBottom>Informações Adicionais</Typography>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Input 
                                            label="Observação"
                                            name="description"
                                            onChange={formik.handleChange}
                                            value={formik.values.description}
                                            helperText={formik.touched.description && formik.errors.description}
                                            error={!!formik.touched.description && !!formik.errors.description}
                                            onBlur={formik.handleBlur}
                                            multiline
                                            fullWidth
                                        />
                                    </Grid>

                                    <Grid item xs={12} sm={6} md={4}>
                                        <FormControlLabel 
                                            control={
                                                <Checkbox 
                                                    checked={formik.values.to_receive}
                                                    onChange={event => formik.setFieldValue('to_receive', event.target.checked)}
                                                    name="to_receive" 
                                                />
                                            } 
                                            label="Á receber" 
                                        />
                                        {formik.values.to_receive && (
                                            <DatePicker 
                                                label="Data recebimento"
                                                value={formik.values.income_due_date ? moment(formik.values.income_due_date) : null}
                                                onChange={(value) => formik.setFieldValue('income_due_date', value?.toDate(), true)}
                                            />
                                        )}
                                    </Grid>

                                    <Grid item xs={12}>
                                        <Paper elevation={3} sx={{ p: 2, borderRadius: 2 }}>
                                            <Grid container spacing={2}>
                                                <Grid item xs={12}>
                                                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                                                        Resumo
                                                    </Typography>
                                                </Grid>
                                                
                                                <Grid item xs={12}>
                                                    <Box sx={{
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        gap: 1.5,
                                                    }}>
                                                        <Box sx={{
                                                            display: 'flex',
                                                            justifyContent: 'space-between',
                                                            p: 1,
                                                            borderBottom: '1px solid #e0e0e0'
                                                        }}>
                                                            <Typography  fontWeight={500}>Custo Total:</Typography>
                                                            <Typography  color="text.secondary">{formatMoney(totalCost)}</Typography>
                                                        </Box>

                                                        {me?.role === 'ADMIN' && (<Box sx={{
                                                            display: 'flex',
                                                            justifyContent: 'space-between',
                                                            p: 1,
                                                            borderBottom: '1px solid #e0e0e0'
                                                        }}>
                                                            <Typography  fontWeight={500}>Lucro Total:</Typography>
                                                            <Typography color="text.secondary">
                                                                {formatMoney(currency(formik.values.sell_price).subtract(totalCost).value)}
                                                            </Typography>
                                                        </Box>)}


                                                        <Box sx={{
                                                            display: 'flex',
                                                            justifyContent: 'space-between',
                                                            p: 1
                                                        }}>
                                                            <Typography fontWeight={500}>Milheiro Vendido:</Typography>
                                                            <Typography color="text.secondary">
                                                                {formatMoney(formik.values.sell_price  && formik.values.amount ? soldCostPerK : 0)}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                </Grid>
                                            </Grid>
                                        </Paper>
                                    </Grid>

                                    <Grid item xs={12} marginTop={2}>
                                        <Button 
                                            variant="contained" 
                                            color="primary" 
                                            fullWidth 
                                            onClick={handleSubmit}
                                            disabled={isSubmitting}
                                        >
                                            {isSubmitting ? 'Processando...' : 'Finalizar Pedido'}
                                        </Button>
                                    </Grid>

                                </Grid>
                            </form>
                        </Paper>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <Paper elevation={1} style={{border: '1px solid #e0e0e0'}}>
                            <Grid container padding={2} spacing={2}>
                                <Grid item xs={12} marginBottom={2}>
                                    <Typography textAlign={"center"} variant="h5">Detalhes da Oferta</Typography>
                                </Grid>
                                <Grid container direction="row" spacing={2} paddingX={2} paddingY={0.5}>
                                    <Grid item xs={4}>
                                        <Grid container direction="column">
                                            <Grid item xs={2}>
                                                <Typography fontWeight="bold">
                                                    ID:
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={10}>
                                                <Link href={`/oferta/${bid?.data?.id}`}>
                                                    # {bid?.data?.id}
                                                </Link>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                    <Grid item xs={4}>
                                        <Grid container direction="column">
                                            <Grid item xs={2}>
                                                <Typography fontWeight="bold">
                                                    Data:
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={10}>
                                                <Typography>
                                                    {moment(bid?.data?.created_at).format('DD/MM/YYYY HH:mm')}
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                    <Grid item xs={4}>
                                        <Grid container direction="column">
                                            <Grid item xs={4}>
                                                <Typography fontWeight="bold">
                                                    Fornecedor:
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={8}>
                                                <Typography>
                                                    {bid?.data?.user?.name}
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Grid container direction="row" spacing={2} paddingX={2} paddingY={0.5}>
                                    <Grid item  xs={4} >
                                        <Grid container direction="column">
                                            <Grid item xs={4}>
                                                <Typography fontWeight="bold">
                                                    Companhia:
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={8}>
                                                <Typography>
                                                    {COMPANY_OPTIONS.find(item => item.value === bid?.data?.company)?.label}
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                    <Grid item  xs={4}>
                                        <Grid container direction="column">
                                            <Grid item xs={4}>
                                                <Typography fontWeight="bold">
                                                    Quantidade:
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={8}>
                                                <Typography>
                                                    {Number(bid?.data?.amount).toLocaleString('pt-BR')}
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                    <Grid item  xs={4}>
                                    <Grid container direction="column">
                                        <Grid item xs={4}>
                                            <Typography fontWeight="bold">
                                                CPFs Disponíveis:
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={8}>
                                            <Typography>
                                                {bid?.data?.pax === null ? '∞' : bid?.data?.pax}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Grid>
                                </Grid>
                                <Grid item xs={12}>
                                    <Grid container direction="column">
                                        <Grid item xs={4}>
                                            <Typography fontWeight="bold">
                                                Valor do Milheiro:
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={8}>
                                            <Typography>
                                                R$ {Number(bid?.data?.price_per_k).toFixed(2)}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Grid item xs={12}>
                                    <Grid container direction="column">
                                        <Grid item xs={12}>
                                            <ButtonGroup fullWidth>
                                                <Button
                                                    variant="contained"
                                                    color="primary"
                                                    startIcon={<WhatsAppIcon />}
                                                    onClick={() => 
                                                        window.open(`https://api.whatsapp.com/send?phone=${bid?.data?.user?.whatsapp}&text=${messageText}`, '_blank')
                                                    }
                                                    sx={{borderRadius: 0}}
                                                    disabled={!bid?.data?.user?.whatsapp}
                                                >
                                                    WHATSAPP
                                                </Button>
                                                <Button
                                                    variant="contained"
                                                    color="primary"
                                                    startIcon={<TelegramIcon />}
                                                    onClick={() => 
                                                        window.open(`https://t.me/${bid?.data?.user?.telegram.replace('@', '')}?text=${messageText}`, '_blank')
                                                    }
                                                    disabled={!bid?.data?.user?.telegram}
                                                    sx={{borderRadius: 0}}
                                                >
                                                    TELEGRAM
                                                </Button>
                                            </ButtonGroup>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Paper>
                        <Paper elevation={1} style={{ marginTop: 30, border: '1px solid #e0e0e0' }}>
                            <Grid container padding={2} spacing={2}>
                                <Grid item xs={12} marginBottom={2}>
                                    <Typography textAlign={"center"} variant="h5">Dados da Conta</Typography>
                                </Grid>
                                <Grid container direction="row" spacing={2} paddingX={2} paddingY={0.5}>

                                <Grid container direction="row" spacing={2} paddingX={2} paddingY={0.5}>
                                    <Grid item xs={6}>
                                        <Grid container direction="column">
                                            <Grid item xs={4}>
                                                <Typography fontWeight="bold">
                                                    Login:
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={8}>
                                                <Typography>
                                                    {bid?.data?.account?.login}
                                                    <IconButton size="small" sx={{ml: 1}} color="primary" onClick={() => { 
                                                        navigator?.clipboard?.writeText(bid?.data?.account?.login)
                                                        enqueueSnackbar('Copiado!', {variant: 'success'})
                                                    }}>
                                                        <ContentCopy />
                                                    </IconButton>
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                    <Grid item xs={3}>
                                        <Grid container direction="column">
                                            <Grid item xs={4}>
                                                <Typography fontWeight="bold">
                                                    Senha:
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={8}>
                                                <Typography>
                                                {
                                                    passwordData ? (
                                                        <Fragment>
                                                            {passwordData.password}
                                                            <IconButton size="small" sx={{ml: 1}} color="primary" onClick={() => { 
                                                                navigator?.clipboard?.writeText(passwordData.password)
                                                                enqueueSnackbar('Copiado!', {variant: 'success'})
                                                            }}>
                                                                <ContentCopy />
                                                            </IconButton>
                                                        </Fragment>
                                                    ) : (
                                                    <div style={{cursor: 'pointer'}} onClick={handleRevealPassword}>
                                                        <VisibilityIcon fontSize="inherit" />
                                                    </div>
                                                    )
                                                }
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                    <Grid item xs={3}>
                                        <Grid container direction="column">
                                            <Grid item xs={4}>
                                                <Typography fontWeight="bold">
                                                    Contrassenha:
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={8}>
                                                <Typography>
                                                {
                                                    passwordData ? (
                                                        <Fragment>
                                                            {passwordData.secondary_password ?? '-'}
                                                            {passwordData.secondary_password && (<IconButton size="small" sx={{ml: 1}} color="primary" onClick={() => { 
                                                                navigator?.clipboard?.writeText(passwordData.secondary_password)
                                                                enqueueSnackbar('Copiado!', {variant: 'success'})
                                                            }}>
                                                                <ContentCopy />
                                                            </IconButton>)}
                                                        </Fragment>
                                                    ) : (
                                                    <div style={{cursor: 'pointer'}} onClick={handleRevealPassword}>
                                                        <VisibilityIcon fontSize="inherit" />
                                                    </div>
                                                    )
                                                }
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Grid container direction="row" spacing={2} paddingX={2} paddingY={0.5}>
                                    <Grid item xs={6}>
                                        <Grid container direction="column">
                                            <Grid item xs={4}>
                                                <Typography fontWeight="bold">
                                                    E-mail do Titular:
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={8}>
                                                <Typography>
                                                    {bid?.data?.account?.holder_email}
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                    <Grid item xs={3}>
                                        <Grid container direction="column">
                                            <Grid item xs={4}>
                                                <Typography fontWeight="bold">
                                                    Nome do Titular:
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={8}>
                                                <Typography>
                                                    {bid?.data?.account?.holder_name}
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                    
                                    <Grid item xs={3}>
                                        <Grid container direction="column">
                                            <Grid item xs={4}>
                                                <Typography fontWeight="bold">
                                                    CPF do Titular:
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={8}>
                                                <Typography>
                                                    {formatCPF(bid?.data?.account?.holder_cpf || '')}
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Grid item xs={12}>
                                    <Grid container direction="column">
                                        <Grid item xs={4}>
                                            <Typography fontWeight="bold">
                                                Data de Nasc. do Titular:
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={8}>
                                            <Typography>
                                                {formatDate(bid?.data?.account?.holder_birthday || '')}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Grid>
                                </Grid>
                            </Grid>
                        </Paper>
                        {bid.data.program.needs_email_redirect && (
                            <Paper elevation={1} style={{ marginTop: 30, border: '1px solid #e0e0e0' }}>
                            <Grid container padding={2} spacing={2}>
                                <Grid item xs={12} marginBottom={2}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Email color="primary" />
                                        <Typography textAlign={"center"} variant="h5">Monitor de Tokens</Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={12}>
                                    <Box sx={{ 
                                        height: 300, 
                                        overflowY: 'auto',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 1
                                    }}>
                                        {(readyState === ReadyState.CONNECTING) ? (
                                            <Box sx={{ 
                                                display: 'flex', 
                                                justifyContent: 'center', 
                                                alignItems: 'center', 
                                                height: '100%' 
                                            }}>
                                                <Typography color="text.secondary">
                                                    Conectando...
                                                </Typography>
                                            </Box>
                                        ) : accountTokens.length === 0 ? (
                                            <Box sx={{ 
                                                display: 'flex', 
                                                justifyContent: 'center', 
                                                alignItems: 'center', 
                                                height: '100%' 
                                            }}>
                                                <Typography color="text.secondary">
                                                    Aguardando novos tokens...
                                                </Typography>
                                            </Box>
                                        ) : (
                                            accountTokens.map((token, index) => (
                                                <Alert 
                                                    key={token} 
                                                    severity="info" 
                                                    sx={{ 
                                                        width: '100%',
                                                        '& .MuiAlert-message': { width: '100%' }
                                                    }}
                                                >
                                                    <Box sx={{ 
                                                        display: 'flex', 
                                                        flexDirection: 'column', 
                                                        gap: 0.5 
                                                    }}>
                                                        <Box sx={{ 
                                                            display: 'flex', 
                                                            alignItems: 'center', 
                                                            gap: 1 
                                                        }}>
                                                        
                                                            <Typography variant="body2">
                                                                {token}
                                                            </Typography>
                                                            <IconButton 
                                                                size="small" 
                                                                onClick={() => {
                                                                    navigator.clipboard.writeText(token);
                                                                    enqueueSnackbar('Código copiado!', { variant: 'success' });
                                                                }}
                                                                sx={{ 
                                                                    p: 0.5,
                                                                    '&:hover': { backgroundColor: 'transparent' }
                                                                }}
                                                            >
                                                                <ContentCopy fontSize="small" />
                                                            </IconButton>
                                                        </Box>
                                                    </Box>
                                                </Alert>
                                            ))
                                        )}
                                    </Box>
                                </Grid>
                            </Grid>
                        </Paper>)}
                    </Grid>
                </Grid>
            )}

            {formik.values.card_id && (
                <CreditCardModal
                    open={creditCardModalVisibility}
                    onClose={creditCardModalVisibilityControls.setFalse} 
                    cardId={Number(formik.values.card_id)}
                />
            )}

            {createCustomerModalVisibility && (
                <CreateCustomerModal 
                    open={createCustomerModalVisibility}
                    onClose={createCustomerModalVisibilityControls.setFalse}
                    onSubmit={async (customerId) => {
                        await refetchCustomers()
                        formik.setFieldValue('customer_id', customerId)
                    }}
                />
            )}
        </Layout>
    )
}